import { useState, type FC } from 'react';
import { Check, FileText, Undo2 } from 'lucide-react';
import { Card } from '@ui/card';
import { Button } from '@ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ui/dialog';
import { Skeleton } from '@ui/skeleton';
import { cn } from '@shared/lib/utils';
import { API_DOCS, INSURANCE_DOCUMENTS } from '../consts/apiDocs.const';
import type { InsuranceCardProps } from '../models/types';
import { formatCheckoutPrice } from '../utils/formatCheckoutPrice';

export const InsuranceCard: FC<InsuranceCardProps> = ({
  premium,
  selected,
  isLoading,
  onSelect,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  return (
    <>
      <Card
        className={cn(
          'relative mt-2 min-h-20 gap-0 overflow-hidden rounded-2xl p-0 transition-all',
          {
            'border-0 bg-gradient-to-br from-insurance-primary-hover to-insurance-selected-to shadow-lg shadow-insurance-primary-hover/30':
              selected,
            'border-[1.5px] border-insurance-border bg-gradient-to-br from-insurance-surface-from to-insurance-surface-to shadow-md shadow-insurance-primary-hover/10':
              !selected,
          }
        )}
      >
        <Button
          type="button"
          variant="ghost"
          aria-label={selected ? 'Убрать защиту билетов' : 'Добавить защиту билетов'}
          aria-pressed={selected}
          className="absolute inset-0 z-0 h-auto w-auto cursor-pointer rounded-2xl p-0 hover:bg-transparent hover:text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-insurance-primary-hover focus-visible:ring-offset-2"
          onClick={onSelect}
        />

        <div className="pointer-events-none relative z-10 flex min-h-20 items-center gap-4 px-5 py-4">
          <div
            className={cn(
              'flex size-12 shrink-0 items-center justify-center rounded-[14px] text-primary-foreground',
              {
                'bg-primary-foreground/20': selected,
                'bg-insurance-primary-hover': !selected,
              }
            )}
          >
            <Undo2 className="size-[26px]" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p
                className={cn('text-sm font-semibold leading-5', {
                  'text-primary-foreground': selected,
                  'text-foreground': !selected,
                })}
              >
                Полный возврат
              </p>
              {selected && (
                <span className="rounded-full bg-primary-foreground/25 px-2 py-0.5 text-[11px] font-semibold leading-4 text-primary-foreground">
                  Добавлено
                </span>
              )}
            </div>
            <p
              className={cn('mt-0.5 truncate text-xs font-medium leading-4', {
                'text-primary-foreground/75': selected,
                'text-muted-foreground': !selected,
              })}
            >
              Вернем полную стоимость билетов, если не сможете пойти на мероприятие
            </p>
          </div>
          <div className="flex min-w-22 shrink-0 flex-col items-end gap-1.5">
            {premium !== null && (
              <span
                className={cn('text-base font-bold leading-6 transition-opacity', {
                  'text-primary-foreground': selected,
                  'text-foreground': !selected,
                  'opacity-60': isLoading,
                })}
              >
                +{formatCheckoutPrice(premium)} ₽
              </span>
            )}
            {premium === null && isLoading && <Skeleton className="h-6 w-20" />}
            <Button
              type="button"
              variant="link"
              className={cn(
                'pointer-events-auto relative z-20 h-auto cursor-pointer p-0 text-[11px] font-medium leading-4 underline',
                {
                  'text-primary-foreground/70 hover:text-primary-foreground': selected,
                  'text-insurance-primary-strong hover:text-insurance-primary-strong-hover':
                    !selected,
                }
              )}
              onClick={event => {
                event.stopPropagation();
                setIsDetailsOpen(true);
              }}
            >
              Подробнее
            </Button>
          </div>
          <span
            aria-hidden="true"
            className={cn('flex size-6 shrink-0 items-center justify-center rounded-full', {
              'border-[1.5px] border-primary-foreground bg-primary-foreground': selected,
              'border-[1.5px] border-insurance-primary-hover': !selected,
            })}
          >
            {selected && (
              <Check className="size-[13px] text-insurance-selected-to" strokeWidth={3} />
            )}
          </span>
        </div>
      </Card>

      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent
          className="max-h-[90vh] overscroll-contain overflow-y-auto"
          overlayClassName="bg-foreground/60"
        >
          <DialogHeader>
            <DialogTitle className="pr-6 leading-6">
              Страхование на случай расходов, связанных с возвратом билетов
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 text-sm leading-5 text-muted-foreground">
            <section>
              <p>
                Страховка поможет вернуть стоимость билета, если вы не сможете посетить мероприятие
                из-за непредвиденных обстоятельств:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>вашей болезни или травмы, а также болезни или травмы близкого человека</li>
                <li>семейных обстоятельств</li>
                <li>неожиданного изменения рабочего графика</li>
                <li>коммунальной аварии</li>
                <li>проблем с транспортом</li>
                <li>вызова в суд или другие государственные органы</li>
              </ul>
            </section>

            <section>
              <p>Страховка не действует в следующих случаях:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>мероприятие уже началось</li>
                <li>мероприятие отменено или перенесено организатором</li>
                <li>
                  на момент оформления страховки вам не исполнилось 18 лет или вы не являетесь
                  гражданином Российской Федерации
                </li>
                <li>
                  невозможность посетить мероприятие связана с участием в противоправных действиях
                </li>
                <li>в иных случаях, предусмотренных правилами страхования</li>
              </ul>
            </section>

            <section>
              <p className="font-medium text-foreground">Как получить выплату:</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5">
                <li>
                  Перейдите в личный кабинет по ссылке{' '}
                  <a
                    href="https://widget-account.colistasystems.ru"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-insurance-primary hover:text-insurance-primary-hover hover:underline"
                  >
                    widget-account.colistasystems.ru
                  </a>{' '}
                  и авторизуйтесь: укажите адрес электронной почты, использованный при покупке
                  билетов, нажмите «Получить код» и введите код из письма.
                </li>
                <li>
                  В разделе «Мои билеты» нажмите «Возврат билетов», ознакомьтесь с правилами и
                  нажмите «Оформить возврат». Укажите номер заказа и отметьте билеты.
                </li>
                <li>
                  Выберите причину обращения. Если выбрали «Другое», опишите обстоятельства,
                  препятствующие посещению мероприятия, в свободной форме. Подать обращение нужно не
                  позднее планового времени начала мероприятия.
                </li>
                <li>
                  Мы передадим ваше обращение страховщику — ООО «Дефанс Страхование». Предоставьте
                  документы по запросу страховой компании.
                </li>
                <li>
                  Получите выплату на банковский счёт в течение 15 рабочих дней после предоставления
                  полного комплекта документов. Размер выплаты — разница между стоимостью билета
                  (без сервисного сбора) и суммой, возвращённой по правилам возврата билетов.
                </li>
              </ol>
            </section>

            <section>
              <p className="font-medium text-foreground">Как отказаться от полиса:</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  отказаться от полиса можно в течение 14 календарных дней с даты его оформления;
                </li>
                <li>
                  подайте заявление напрямую в страховую компанию на её сайте:{' '}
                  <a
                    href="https://www.defense.insure/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-insurance-primary hover:text-insurance-primary-hover hover:underline"
                  >
                    https://www.defense.insure/
                  </a>
                </li>
                <li>
                  если страхование ещё не начало действовать, страховая премия возвращается в полном
                  объёме;
                </li>
                <li>
                  если срок страхования уже начался, сумма возврата рассчитывается за вычетом части
                  страховой премии за период действия страховки;
                </li>
                <li>после окончания срока страхования страховая премия не возвращается;</li>
                <li>
                  страховая премия возвращается в течение 7 рабочих дней с даты получения
                  страховщиком вашего обращения;
                </li>
                <li>возврат билета не прекращает договор страхования автоматически.</li>
              </ul>
            </section>

            <section className="space-y-1">
              <p>Территория действия: РФ.</p>
              <p>Страховщик: ООО «Дефанс Страхование», лицензии СИ №4104, СЛ №4104.</p>
            </section>

            <section>
              <p className="font-medium text-foreground">Документы</p>
              <div className="mt-2 space-y-2">
                {INSURANCE_DOCUMENTS.map(document => (
                  <a
                    key={document.type}
                    href={API_DOCS.INSURANCE(document.type)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex w-fit items-center gap-2 text-insurance-primary hover:text-insurance-primary-hover hover:underline"
                  >
                    <FileText className="size-4 shrink-0" aria-hidden="true" />
                    {document.title}
                  </a>
                ))}
              </div>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
